import { TalkingHeadActorEventIdn, TalkingHeadService } from '@newo/api-client';
import { runtimeConfig } from '@/constants';

type AgentRequestCredentials = {
	clientSecret?: string | null;
	connectorIdn?: string | null;
	externalId?: string | null;
};

export class AgentRequestRefreshService {
	private readonly reconnectStormWindowMs = 5 * 60 * 1000;

	private readonly reconnectStormAttemptThreshold = 3;

	private readonly reconnectProtectionThrottleMs = 5 * 60 * 1000;

	private readonly reconnectStableRecoveryMs = 10 * 60 * 1000;

	private hasSentInitialAgentRequest = false;

	private isAgentRequestPending = false;

	private isReconnectProtectionActive = false;

	private lastReconnectRefreshAt?: number;

	private reconnectAttemptTimestamps: number[] = [];

	private requestAbortController?: AbortController;

	private stableRecoveryTimer?: ReturnType<typeof globalThis.setTimeout>;

	private trailingReconnectRefreshTimer?: ReturnType<
		typeof globalThis.setTimeout
	>;

	private trailingReconnectCredentials?: AgentRequestCredentials;

	sendInitialAgentRequest(credentials: AgentRequestCredentials): void {
		if (this.hasSentInitialAgentRequest) {
			return;
		}

		if (this.sendAgentRequest(credentials)) {
			this.hasSentInitialAgentRequest = true;
		}
	}

	sendProtectedReconnectAgentRequest(
		credentials: AgentRequestCredentials,
		timestamp = Date.now()
	): void {
		this.recordReconnectAttempt(timestamp);
		this.resetStableRecoveryTimer();

		if (this.hasReconnectStorm()) {
			this.enterReconnectProtection();
		}

		if (!this.isReconnectProtectionActive) {
			this.sendReconnectAgentRequest(credentials, timestamp);
			return;
		}

		this.sendProtectedReconnectRefresh(credentials, timestamp);
	}

	cleanup(): void {
		this.clearStableRecoveryTimer();
		this.clearTrailingReconnectRefresh();
		this.requestAbortController?.abort();
		this.requestAbortController = undefined;
		this.hasSentInitialAgentRequest = false;
		this.isAgentRequestPending = false;
		this.isReconnectProtectionActive = false;
		this.lastReconnectRefreshAt = undefined;
		this.reconnectAttemptTimestamps = [];
	}

	private recordReconnectAttempt(timestamp: number): void {
		this.reconnectAttemptTimestamps = [
			...this.reconnectAttemptTimestamps.filter(
				(reconnectTimestamp) =>
					timestamp - reconnectTimestamp <= this.reconnectStormWindowMs
			),
			timestamp
		];
	}

	private hasReconnectStorm(): boolean {
		return (
			this.reconnectAttemptTimestamps.length >=
			this.reconnectStormAttemptThreshold
		);
	}

	private enterReconnectProtection(): void {
		this.isReconnectProtectionActive = true;
	}

	private sendProtectedReconnectRefresh(
		credentials: AgentRequestCredentials,
		timestamp: number
	): void {
		if (
			this.lastReconnectRefreshAt == null ||
			timestamp - this.lastReconnectRefreshAt >=
				this.reconnectProtectionThrottleMs
		) {
			this.clearTrailingReconnectRefresh();
			this.sendReconnectAgentRequest(credentials, timestamp);
			return;
		}

		this.scheduleTrailingReconnectRefresh(credentials, timestamp);
	}

	private resetStableRecoveryTimer(): void {
		this.clearStableRecoveryTimer();
		this.stableRecoveryTimer = globalThis.setTimeout(() => {
			this.recoverFromStableConnection();
		}, this.reconnectStableRecoveryMs);
	}

	private recoverFromStableConnection(): void {
		this.isReconnectProtectionActive = false;
		this.lastReconnectRefreshAt = undefined;
		this.reconnectAttemptTimestamps = [];
		this.clearTrailingReconnectRefresh();
		this.stableRecoveryTimer = undefined;
	}

	private clearStableRecoveryTimer(): void {
		if (this.stableRecoveryTimer == null) {
			return;
		}

		globalThis.clearTimeout(this.stableRecoveryTimer);
		this.stableRecoveryTimer = undefined;
	}

	private sendAgentRequest(credentials: AgentRequestCredentials): boolean {
		const { clientSecret, connectorIdn, externalId } = credentials;

		if (!clientSecret || !connectorIdn || !externalId) {
			return false;
		}

		if (this.isAgentRequestPending) {
			return false;
		}

		const requestAbortController = new AbortController();

		this.isAgentRequestPending = true;
		this.requestAbortController = requestAbortController;

		void TalkingHeadService.sendActorEvent(
			{
				client_secret: clientSecret,
				connector_idn: connectorIdn,
				external_id: externalId,
				event_idn: TalkingHeadActorEventIdn.AgentRequest,
				arguments: []
			},
			{
				signal: requestAbortController.signal,
				...(runtimeConfig.isRemoteBuild ? {} : { skipAuth: true })
			}
		)
			.catch((error: unknown) => {
				if (requestAbortController.signal.aborted) {
					return;
				}

				console.warn('agent_request failed:', error);
			})
			.finally(() => {
				if (this.requestAbortController === requestAbortController) {
					this.requestAbortController = undefined;
				}

				this.isAgentRequestPending = false;
			});

		return true;
	}

	private sendReconnectAgentRequest(
		credentials: AgentRequestCredentials,
		timestamp: number
	): void {
		if (this.sendAgentRequest(credentials)) {
			this.lastReconnectRefreshAt = timestamp;
		}
	}

	private scheduleTrailingReconnectRefresh(
		credentials: AgentRequestCredentials,
		timestamp: number
	): void {
		this.trailingReconnectCredentials = credentials;

		if (this.trailingReconnectRefreshTimer != null) {
			return;
		}

		const nextReconnectRefreshAt =
			this.lastReconnectRefreshAt == null
				? timestamp
				: this.lastReconnectRefreshAt + this.reconnectProtectionThrottleMs;

		this.trailingReconnectRefreshTimer = globalThis.setTimeout(
			() => {
				this.trailingReconnectRefreshTimer = undefined;
				const trailingReconnectCredentials = this.trailingReconnectCredentials;
				this.trailingReconnectCredentials = undefined;

				if (!trailingReconnectCredentials) {
					return;
				}

				this.sendReconnectAgentRequest(
					trailingReconnectCredentials,
					Date.now()
				);
			},
			Math.max(nextReconnectRefreshAt - timestamp, 0)
		);
	}

	private clearTrailingReconnectRefresh(): void {
		if (this.trailingReconnectRefreshTimer == null) {
			return;
		}

		globalThis.clearTimeout(this.trailingReconnectRefreshTimer);
		this.trailingReconnectRefreshTimer = undefined;
		this.trailingReconnectCredentials = undefined;
	}
}

export const agentRequestRefreshService = new AgentRequestRefreshService();
